import { useState, useRef, useEffect } from 'react';

export default function AudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState('');
  const [noiseReductionLevel, setNoiseReductionLevel] = useState(0.05);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const analyserNodeRef = useRef(null);
  const filterNodeRef = useRef(null);
  const streamRef = useRef(null);
  const processorNodeRef = useRef(null);

  const startRecording = async () => {
    try {
      // Reset audio chunks
      audioChunksRef.current = [];

      // Get media stream
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up Web Audio API for noise reduction
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Create source from microphone stream
      const source = audioContext.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      // Create analyzer to detect noise levels
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyserNodeRef.current = analyser;

      // Create filter for noise reduction
      const filter = audioContext.createBiquadFilter();
      filter.type = 'lowshelf';
      filter.frequency.value = 150;
      filter.gain.value = -20;
      filterNodeRef.current = filter;

      // Create a second filter to reduce high-frequency noise
      const highFilter = audioContext.createBiquadFilter();
      highFilter.type = 'highshelf';
      highFilter.frequency.value = 3000;
      highFilter.gain.value = -15;

      // Connect the audio graph
      source.connect(filter);
      filter.connect(highFilter);
      highFilter.connect(analyser);

      // Create a new output stream from our processed audio
      const destination = audioContext.createMediaStreamDestination();
      analyser.connect(destination);

      // Create a media recorder with the processed stream
      const mediaRecorder = new MediaRecorder(destination.stream);
      mediaRecorderRef.current = mediaRecorder;

      // Handle data available event
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioURL(audioUrl);
      };

      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Error starting recording: ' + error.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }

    // Stop the stream tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
  };

  const handleNoiseReductionChange = (e) => {
    const value = parseFloat(e.target.value);
    setNoiseReductionLevel(value);

    // Adjust filter settings based on noise reduction level
    if (filterNodeRef.current) {
      filterNodeRef.current.gain.value = -20 - (value * 40); // More negative = more reduction
    }
  };

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center p-6 max-w-md mx-auto bg-white rounded-xl shadow-md">
      <h2 className="text-xl font-bold mb-4">Audio Recorder with Noise Reduction</h2>

      <div className="flex flex-col w-full mb-6">
        <label className="mb-1 text-sm text-gray-600">
          Noise Reduction Level: {Math.round(noiseReductionLevel * 100)}%
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={noiseReductionLevel}
          onChange={handleNoiseReductionChange}
          className="w-full"
        />
      </div>

      <div className="flex space-x-4 mb-6">
        <button
          onClick={startRecording}
          disabled={isRecording}
          className={`px-4 py-2 rounded ${
            isRecording
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          Start Recording
        </button>

        <button
          onClick={stopRecording}
          disabled={!isRecording}
          className={`px-4 py-2 rounded ${
            !isRecording
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-red-500 hover:bg-red-600 text-white'
          }`}
        >
          Stop Recording
        </button>
      </div>

      {isRecording && (
        <div className="w-full h-8 bg-red-100 rounded-full overflow-hidden mb-6">
          <div className="h-full bg-red-500 animate-pulse"></div>
        </div>
      )}

      {audioURL && (
        <div className="w-full">
          <h3 className="text-lg font-semibold mb-2">Recorded Audio</h3>
          <audio src={audioURL} controls className="w-full"></audio>
          <a
            href={audioURL}
            download="recording.wav"
            className="block text-center mt-2 text-blue-500 hover:text-blue-700"
          >
            Download Recording
          </a>
        </div>
      )}
    </div>
  );
}